import { all, call, put, takeEvery } from 'redux-saga/effects';
import Api, { executeAction } from 'src/helpers/api';
import { sendRequest } from 'src/helpers/saga';
import { logger } from 'src/helpers/logger';
import notifications from 'src/redux/notifications/actions';
import { t } from 'src/i18n';
import { isSuccess } from '../apiStatus';
import actions from './actions';
import type {
  IArchiveRequestPayload,
  IDeleteRequestPayload,
  IPublishRequestPayload,
  IRenameRequestPayload,
} from './types';

type MutationAction<P> = { type: string; payload: P };

// Exported so a test can pin the route: `getPath` matches path segments positionally, so an id in
// any other position resolves silently to a different URL.
export const PUBLISH_ROUTE = 'pricing/engine_versions/:id/publish';

export const publishApi = new Api(PUBLISH_ROUTE);

// Without it a 422's validations map never reaches the caller and an explained 403 becomes a
// permission toast, so every publish refusal would degrade into "something went wrong".
const PRESERVE_STATUS = { preserveErrorStatus: true };

function errorNotification(message: string) {
  return notifications.addNotification({
    headline: t('dash.common.notification.error_headline'),
    type: 'error',
    message,
  });
}

function genericErrorNotification() {
  return errorNotification(t('dash.common.notification.something_went_wrong'));
}

// Rename and delete are refused with a validations map or an explained 403; the generic
// "you do not have permission" is wrong there — the administrator does hold the permission.
const refusalReason = (
  result: { validations?: Record<string, string[]>; message?: string } | undefined,
): string | null => {
  const messages = Object.values(result?.validations ?? {}).flat();

  if (messages.length) return messages.join(' ');

  return result?.message ?? null;
};

export function* renameRequest({ payload }: MutationAction<IRenameRequestPayload>): Generator<any, void, any> {
  try {
    const result = yield call(
      sendRequest,
      executeAction,
      { url: payload.url, method: payload.method, data: { engine: { name: payload.name } } },
      undefined,
      undefined,
      undefined,
      PRESERVE_STATUS,
    );

    if (isSuccess(result)) {
      yield put(actions.renameSuccess());
      yield put(notifications.success(t('dash.admin.rateEngine.notifications.rename_success', { name: payload.name })));

      return;
    }

    yield put(actions.renameError());

    // The dialog closes on a refusal, so the toast is the only thing left carrying the reason.
    const refusal = refusalReason(result);

    if (refusal) {
      yield put(errorNotification(refusal));

      return;
    }

    // An undefined result was already toasted by sendRequest: never two toasts for one failure.
    if (result) yield put(genericErrorNotification());
  } catch (error) {
    logger.error(error);
    yield put(actions.renameError());
    yield put(genericErrorNotification());
  }
}

export function* deleteRequest({ payload }: MutationAction<IDeleteRequestPayload>): Generator<any, void, any> {
  try {
    const result = yield call(
      sendRequest,
      executeAction,
      { url: payload.url, method: payload.method },
      undefined,
      undefined,
      undefined,
      PRESERVE_STATUS,
    );

    if (isSuccess(result)) {
      yield put(actions.deleteSuccess());
      yield put(notifications.success(t('dash.admin.rateEngine.notifications.delete_success')));

      return;
    }

    yield put(actions.deleteError());

    const refusal = refusalReason(result);

    if (refusal) {
      yield put(errorNotification(refusal));

      return;
    }

    if (result) yield put(genericErrorNotification());
  } catch (error) {
    logger.error(error);
    yield put(actions.deleteError());
    yield put(genericErrorNotification());
  }
}

export function* archiveRequest({ payload }: MutationAction<IArchiveRequestPayload>): Generator<any, void, any> {
  try {
    const result = yield call(
      sendRequest,
      executeAction,
      { url: payload.url, method: payload.method },
      notifications.REQUEST_ERROR,
    );

    if (isSuccess(result)) {
      yield put(actions.archiveSuccess());
      yield put(notifications.success(t('dash.admin.rateEngine.notifications.archive_success')));

      return;
    }

    yield put(actions.archiveError());

    if (result) yield put(genericErrorNotification());
  } catch (error) {
    logger.error(error);
    yield put(actions.archiveError());
    yield put(genericErrorNotification());
  }
}

const BLANK_FIELD_ERROR = "can't be blank";

// Only a blank `effective_from` is reworded: the other date errors already name the fix, so
// replacing them would misdirect. Unrecognised validation keys are joined rather than dropped.
const publishRefusal = (
  result: { validations?: Record<string, string[]>; message?: string } | undefined,
): string | null => {
  const validations = result?.validations;
  const dateErrors = validations?.effective_from ?? [];

  if (dateErrors.length) {
    if (dateErrors.some(message => message.includes(BLANK_FIELD_ERROR))) {
      return t('dash.admin.rateEngine.notifications.publish_needs_effective_date');
    }

    // The backend's wording is English prose and arrives untranslated, so it is quoted after a self-contained
    // sentence rather than interpolated into one — splicing produced grammar only in English.
    return `${t('dash.admin.rateEngine.notifications.publish_effective_date_refused')} ${dateErrors.join(' ')}`;
  }

  if (validations?.base?.length) return validations.base.join(' ');

  if (result?.message) return result.message;

  const remaining = Object.values(validations ?? {}).flat();

  return remaining.length ? remaining.join(' ') : null;
};

export function* publishRequest({ payload }: MutationAction<IPublishRequestPayload>): Generator<any, void, any> {
  try {
    const result = yield call(
      sendRequest,
      publishApi.createItem,
      { id: payload.id, data: {} },
      undefined,
      undefined,
      undefined,
      PRESERVE_STATUS,
    );

    if (isSuccess(result)) {
      yield put(actions.publishSuccess());
      yield put(notifications.success(t('dash.admin.rateEngine.notifications.publish_success')));

      return;
    }

    yield put(actions.publishError());

    const refusal = publishRefusal(result);

    if (refusal) {
      yield put(errorNotification(refusal));

      return;
    }

    if (result) yield put(genericErrorNotification());
  } catch (error) {
    logger.error(error);
    yield put(actions.publishError());
    yield put(genericErrorNotification());
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actions.RENAME_REQUEST, renameRequest),
    takeEvery(actions.DELETE_REQUEST, deleteRequest),
    takeEvery(actions.ARCHIVE_REQUEST, archiveRequest),
    takeEvery(actions.PUBLISH_REQUEST, publishRequest),
  ]);
}
